import type { LandingEditorElementDefinition } from '../editor/landingEditorRegistry.js';
import { mergeInlineTextDraftIntoProject, type InlineTextDraft } from '../editor/inlineTextCommit.js';
import type { LandingEditorElementFrame } from '../../shared/editor/landingEditorTypes.js';
import type { LandingEditorProject } from '../../shared/editor/landingEditorTypes.js';
import { isLandingCompanyHeroSection, type LandingHomeSectionKey } from '../../shared/editor/landingEditorTypes.js';
import type { RepeatTarget } from './editorPageConfig.js';
import type { StaticPageBuiltInSectionKey } from '../../shared/editor/landingEditorTypes.js';
import type { StaticPageKey } from '../../shared/content/staticPageContent.js';
import {
  patchInlineTextStyleRange,
  rebaseInlineTextStyles,
  replaceInlineTextStyleRange,
  type LandingEditorInlineTextStyle,
} from '../../shared/editor/landingEditorInlineTypography.js';
import type { InlineTextStyleChange } from './editorInlineTypographyControls.js';

export const createEditorProjectOperations = (context: Record<string, any>) => {
  const { activeEditorAudience, activeEditorPage, clampZoom, cloneEditorValue, defaultFrame, definitions, draggedSection, elementIdByClass, expandedSections, fieldSourceForProject, fieldValueForDefinition, frameKeyWithIndex, getOrderedStaticPageBuiltInSections, inlineTextDraft, landingPageContentRepository, locale, matchingRepeatFrameKey, mutableArrayAtPath, normalizeEditableText, onRepeatAdded, onSectionAdded, project, recordLocalizedFieldCommit, repeatIdTargetForElementId, repeatTargetForDefinition, replaceFirstNumericSegment, selectedFieldDefinition, setDraggedSection, setExpandedSections, undoDocument, redoDocument, setInlineTextDraft, setInlineTextGeometry, setMessage, setSelectedElementId, setStatus, setValueAtPath, setZoom, stageLocalizationTextResult, updateField, updateProject, zoom } = context;
  const getActiveSectionOrderArray = (p: LandingEditorProject) =>
      activeEditorPage === 'landing'
        ? [...p.sectionOrder]
        : [...(p.staticSectionOrder?.[activeEditorAudience]?.[activeEditorPage as StaticPageKey] || [])];
  
    const setActiveSectionOrderArray = (p: LandingEditorProject, order: LandingHomeSectionKey[]) => {
      if (activeEditorPage === 'landing') {
        p.sectionOrder = order;
      } else {
        if (!p.staticSectionOrder[activeEditorAudience]) {
          p.staticSectionOrder[activeEditorAudience] = {} as Record<
            StaticPageKey,
            LandingHomeSectionKey[]
          >;
        }
        p.staticSectionOrder[activeEditorAudience][activeEditorPage as StaticPageKey] = order;
      }
    };
  
    const moveSection = (section: LandingHomeSectionKey, direction: -1 | 1) => {
      const nextProject = cloneEditorValue(project);
      const order = getActiveSectionOrderArray(nextProject);
      const index = order.indexOf(section);
      const targetIndex = index + direction;
      if (index < 0 || targetIndex < 0 || targetIndex >= order.length) return;
      const currentSection = order[index];
      const targetSection = order[targetIndex];
      if (!currentSection || !targetSection) return;
      order[index] = targetSection;
      order[targetIndex] = currentSection;
      setActiveSectionOrderArray(nextProject, order);
      updateProject(nextProject);
    };
  
    const setActiveStaticPageBuiltInSectionOrderArray = (
      p: LandingEditorProject,
      order: StaticPageBuiltInSectionKey[],
    ) => {
      if (activeEditorPage === 'landing') return;
      const pageKey = activeEditorPage as StaticPageKey;
      if (!p.staticPageSectionOrder) p.staticPageSectionOrder = {} as LandingEditorProject['staticPageSectionOrder'];
      if (!p.staticPageSectionOrder[activeEditorAudience]) {
        p.staticPageSectionOrder[activeEditorAudience] = {} as Record<StaticPageKey, StaticPageBuiltInSectionKey[]>;
      }
      p.staticPageSectionOrder[activeEditorAudience][pageKey] = order;
    };
  
    const moveStaticPageBuiltInSection = (section: StaticPageBuiltInSectionKey, direction: -1 | 1) => {
      if (activeEditorPage === 'landing') return;
      const nextProject = cloneEditorValue(project);
      const pageKey = activeEditorPage as StaticPageKey;
      const order = getOrderedStaticPageBuiltInSections(nextProject, pageKey, activeEditorAudience);
      const index = order.indexOf(section);
      const targetIndex = index + direction;
      if (index < 0 || targetIndex < 0 || targetIndex >= order.length) return;
      const currentSection = order[index];
      const targetSection = order[targetIndex];
      if (!currentSection || !targetSection) return;
      order[index] = targetSection;
      order[targetIndex] = currentSection;
      setActiveStaticPageBuiltInSectionOrderArray(nextProject, order);
      updateProject(nextProject);
    };
  
    const moveSectionBefore = (section: LandingHomeSectionKey, targetSection: LandingHomeSectionKey) => {
      if (section === targetSection) return;
      const nextProject = cloneEditorValue(project);
      const order = getActiveSectionOrderArray(nextProject);
      const sourceIndex = order.indexOf(section);
      if (sourceIndex < 0) return;
      order.splice(sourceIndex, 1);
      const targetIndex = order.indexOf(targetSection);
      if (targetIndex < 0) return;
      order.splice(targetIndex, 0, section);
      setActiveSectionOrderArray(nextProject, order);
      updateProject(nextProject);
    };
  
    const duplicateSection = (section: LandingHomeSectionKey) => {
      if (activeEditorPage === 'landing' && isLandingCompanyHeroSection(section)) {
        setSelectedElementId(`section.${section}`);
        return;
      }
      const nextProject = cloneEditorValue(project);
      const order = getActiveSectionOrderArray(nextProject);
      const index = order.indexOf(section);
      order.splice(index >= 0 ? index + 1 : order.length, 0, section);
      setActiveSectionOrderArray(nextProject, order);
      updateProject(nextProject);
      setSelectedElementId(`section.${section}`);
      onSectionAdded?.(section, nextProject);
    };
  
    const addSection = (section: LandingHomeSectionKey) => {
      const nextProject = cloneEditorValue(project);
      const initialOrder = getActiveSectionOrderArray(nextProject);
      const replacedHeroIndex = activeEditorPage === 'landing' && isLandingCompanyHeroSection(section)
        ? initialOrder.findIndex(isLandingCompanyHeroSection)
        : -1;
      const order = initialOrder.filter(
        current => activeEditorPage !== 'landing' || !isLandingCompanyHeroSection(section) || !isLandingCompanyHeroSection(current),
      );
      order.splice(replacedHeroIndex >= 0 ? Math.min(replacedHeroIndex, order.length) : order.length, 0, section);
      setActiveSectionOrderArray(nextProject, order);
      updateProject(nextProject);
      setSelectedElementId(`section.${section}`);
      onSectionAdded?.(section, nextProject);
    };
  
    const addSectionBefore = (section: LandingHomeSectionKey, targetSection: LandingHomeSectionKey) => {
      const nextProject = cloneEditorValue(project);
      const initialOrder = getActiveSectionOrderArray(nextProject);
      const targetIndexBeforeReplacement = initialOrder.indexOf(targetSection);
      const order = initialOrder.filter(
        current => activeEditorPage !== 'landing' || !isLandingCompanyHeroSection(section) || !isLandingCompanyHeroSection(current),
      );
      const targetIndex = order.indexOf(targetSection);
      order.splice(targetIndex < 0 ? Math.min(Math.max(targetIndexBeforeReplacement, 0), order.length) : targetIndex, 0, section);
      setActiveSectionOrderArray(nextProject, order);
      updateProject(nextProject);
      setSelectedElementId(`section.${section}`);
    };
  
    // Drag/drop entry point that either reorders a present section or re-adds a removed one.
    const placeSection = (section: LandingHomeSectionKey, targetSection?: LandingHomeSectionKey) => {
      const order = getActiveSectionOrderArray(project);
      if (order.includes(section)) {
        if (targetSection && targetSection !== section) moveSectionBefore(section, targetSection);
        return;
      }
      if (targetSection) addSectionBefore(section, targetSection);
      else addSection(section);
    };
  
    const deleteSection = (section: LandingHomeSectionKey) => {
      const nextProject = cloneEditorValue(project);
      const order = getActiveSectionOrderArray(nextProject);
      const index = order.indexOf(section);
      if (index < 0) return;
      if (activeEditorPage === 'landing' && order.length <= 1) {
        setMessage('Die letzte Section kann nicht gelöscht werden.');
        setStatus('error');
        return;
      }
      order.splice(index, 1);
      setActiveSectionOrderArray(nextProject, order);
      updateProject(nextProject);
      const nextSection = order[index] ?? order[index - 1];
      setSelectedElementId(nextSection ? `section.${nextSection}` : 'page.root');
    };
  
    const duplicateRepeatTarget = (definition: LandingEditorElementDefinition, target: RepeatTarget) => {
      const nextProject = cloneProjectWithInlineTextDraft();
      if (definition.source === 'static') {
        const array = mutableArrayAtPath(fieldSourceForProject(nextProject, definition), target.path);
        const item = array?.[target.index];
        if (array && item !== undefined) array.splice(target.index + 1, 0, cloneEditorValue(item));
      } else {
        for (const localeOption of landingPageContentRepository.locales) {
          const localeContent = nextProject.content[localeOption] as unknown as Record<string, unknown>;
          const array = mutableArrayAtPath(localeContent, target.path);
          const item = array?.[target.index];
          if (!array || item === undefined) continue;
          array.splice(target.index + 1, 0, cloneEditorValue(item));
        }
      }
      shiftRepeatedElementFrames(nextProject, definition.id, 'duplicate');
      updateProject(nextProject);
      const nextElementId = replaceFirstNumericSegment(definition.id, target.index + 1);
      setSelectedElementId(nextElementId);
      onRepeatAdded?.(definition, nextElementId, nextProject);
    };
  
    const deleteRepeatTarget = (definition: LandingEditorElementDefinition, target: RepeatTarget) => {
      const nextProject = cloneProjectWithInlineTextDraft();
      if (definition.source === 'static') {
        const array = mutableArrayAtPath(fieldSourceForProject(nextProject, definition), target.path);
        if (array && target.index >= 0 && target.index < array.length) array.splice(target.index, 1);
      } else {
        for (const localeOption of landingPageContentRepository.locales) {
          const localeContent = nextProject.content[localeOption] as unknown as Record<string, unknown>;
          const array = mutableArrayAtPath(localeContent, target.path);
          if (!array || target.index < 0 || target.index >= array.length) continue;
          array.splice(target.index, 1);
        }
      }
      shiftRepeatedElementFrames(nextProject, definition.id, 'delete');
      updateProject(nextProject);
      setSelectedElementId(definition.parentId ?? (definition.section ? `section.${definition.section}` : 'page.root'));
    };

    const cloneProjectWithInlineTextDraft = () => {
      const nextProject = cloneEditorValue(project) as LandingEditorProject;
      const activeEditor =
        typeof document === 'undefined'
          ? null
          : document.querySelector<HTMLElement>('.vp-editor-inline-textarea');
      const draftForCommit =
        activeEditor && inlineTextDraft?.elementId
          ? { elementId: inlineTextDraft.elementId, value: activeEditor.dataset.value ?? inlineTextDraft.value }
          : inlineTextDraft;
      mergeInlineTextDraftIntoProject(
        nextProject,
        draftForCommit,
        definitions,
        locale,
        fieldSourceForProject,
        normalizeEditableText,
      );
      return nextProject;
    };

    const prepareRepeatedCommentAnchors = (nextProject: LandingEditorProject, target: NonNullable<ReturnType<typeof repeatIdTargetForElementId>>) => {
      for (const definition of definitions) {
        if (matchingRepeatFrameKey(definition.id, target) && !nextProject.editorAnchorIds[definition.id]) {
          nextProject.editorAnchorIds[definition.id] = definition.id;
        }
      }
      return Object.entries(nextProject.editorAnchorIds)
        .map(([key, anchorId]) => ({ key, anchorId, match: matchingRepeatFrameKey(key, target) }))
        .filter((entry): entry is { key: string; anchorId: string; match: { segments: string[]; index: number } } => Boolean(entry.match));
    };

    const shiftRepeatedCommentAnchors = (nextProject: LandingEditorProject, target: NonNullable<ReturnType<typeof repeatIdTargetForElementId>>, mode: 'duplicate' | 'delete') => {
      const entries = prepareRepeatedCommentAnchors(nextProject, target);
      if (mode === 'duplicate') {
        entries.filter(entry => entry.match.index > target.index).sort((left, right) => right.match.index - left.match.index).forEach(entry => {
          delete nextProject.editorAnchorIds[entry.key];
          nextProject.editorAnchorIds[frameKeyWithIndex(entry.match.segments, target, entry.match.index + 1)] = entry.anchorId;
        });
        entries.filter(entry => entry.match.index === target.index).forEach(entry => {
          nextProject.editorAnchorIds[frameKeyWithIndex(entry.match.segments, target, target.index + 1)] = crypto.randomUUID();
        });
        return;
      }
      entries.filter(entry => entry.match.index >= target.index).sort((left, right) => left.match.index - right.match.index).forEach(entry => {
        delete nextProject.editorAnchorIds[entry.key];
        if (entry.match.index > target.index) nextProject.editorAnchorIds[frameKeyWithIndex(entry.match.segments, target, entry.match.index - 1)] = entry.anchorId;
      });
    };
  
    const shiftRepeatedElementFrames = (nextProject: LandingEditorProject, elementId: string, mode: 'duplicate' | 'delete') => {
      const target = repeatIdTargetForElementId(elementId);
      if (!target) return;
      shiftRepeatedCommentAnchors(nextProject, target, mode);
  
      const entries = Object.entries(nextProject.elementFrames)
        .map(([key, frame]) => ({ key, frame, match: matchingRepeatFrameKey(key, target) }))
        .filter((entry): entry is { key: string; frame: LandingEditorElementFrame; match: { segments: string[]; index: number } } =>
          Boolean(entry.match),
        );
  
      if (mode === 'duplicate') {
        const copiedFrames = entries.filter(entry => entry.match.index === target.index);
        entries
          .filter(entry => entry.match.index > target.index)
          .sort((left, right) => right.match.index - left.match.index)
          .forEach(entry => {
            delete nextProject.elementFrames[entry.key];
            nextProject.elementFrames[frameKeyWithIndex(entry.match.segments, target, entry.match.index + 1)] = entry.frame;
          });
        copiedFrames.forEach(entry => {
          nextProject.elementFrames[frameKeyWithIndex(entry.match.segments, target, target.index + 1)] = {
            ...cloneEditorValue(entry.frame),
            x: entry.frame.x + 16,
            y: entry.frame.y + 16,
          };
        });
        return;
      }
  
      entries
        .filter(entry => entry.match.index >= target.index)
        .sort((left, right) => left.match.index - right.match.index)
        .forEach(entry => {
          delete nextProject.elementFrames[entry.key];
          if (entry.match.index > target.index) {
            nextProject.elementFrames[frameKeyWithIndex(entry.match.segments, target, entry.match.index - 1)] = entry.frame;
          }
        });
    };

    const swapRepeatedElementFrames = (
      nextProject: LandingEditorProject,
      elementId: string,
      destinationIndex: number,
    ) => {
      const target = repeatIdTargetForElementId(elementId);
      if (!target || destinationIndex === target.index) return;

      const anchorEntries = prepareRepeatedCommentAnchors(nextProject, target).filter(entry =>
        entry.match.index === target.index || entry.match.index === destinationIndex,
      );
      anchorEntries.forEach(entry => delete nextProject.editorAnchorIds[entry.key]);
      anchorEntries.forEach(entry => {
        const nextIndex = entry.match.index === target.index ? destinationIndex : target.index;
        nextProject.editorAnchorIds[frameKeyWithIndex(entry.match.segments, target, nextIndex)] = entry.anchorId;
      });

      const entries = Object.entries(nextProject.elementFrames)
        .map(([key, frame]) => ({ key, frame, match: matchingRepeatFrameKey(key, target) }))
        .filter(
          (entry): entry is {
            key: string;
            frame: LandingEditorElementFrame;
            match: { segments: string[]; index: number };
          } => Boolean(entry.match && (entry.match.index === target.index || entry.match.index === destinationIndex)),
        );

      entries.forEach(entry => delete nextProject.elementFrames[entry.key]);
      entries.forEach(entry => {
        const nextIndex = entry.match.index === target.index ? destinationIndex : target.index;
        nextProject.elementFrames[frameKeyWithIndex(entry.match.segments, target, nextIndex)] = entry.frame;
      });
    };

    const moveRepeatDefinition = (definition: LandingEditorElementDefinition, direction: -1 | 1) => {
      const target = repeatTargetForDefinition(definition);
      if (!target) return;
      const sourceArray = mutableArrayAtPath(fieldSourceForProject(project, definition), target.path);
      const destinationIndex = target.index + direction;
      if (!sourceArray || destinationIndex < 0 || destinationIndex >= sourceArray.length) return;

      const nextProject = cloneProjectWithInlineTextDraft();
      const swapItems = (array: unknown[] | null) => {
        if (!array || destinationIndex >= array.length) return;
        const sourceItem = array[target.index];
        const destinationItem = array[destinationIndex];
        if (sourceItem === undefined || destinationItem === undefined) return;
        array[target.index] = destinationItem;
        array[destinationIndex] = sourceItem;
      };

      if (definition.source === 'static') {
        swapItems(mutableArrayAtPath(fieldSourceForProject(nextProject, definition), target.path));
      } else {
        for (const localeOption of landingPageContentRepository.locales) {
          const localeContent = nextProject.content[localeOption] as unknown as Record<string, unknown>;
          swapItems(mutableArrayAtPath(localeContent, target.path));
        }
      }

      swapRepeatedElementFrames(nextProject, definition.id, destinationIndex);
      updateProject(nextProject);
      setSelectedElementId(replaceFirstNumericSegment(definition.id, destinationIndex));
    };
  
    const canDuplicateDefinition = (definition: LandingEditorElementDefinition) =>
      definition.kind === 'section' || Boolean(repeatTargetForDefinition(definition));
  
    const canDeleteDefinition = (definition: LandingEditorElementDefinition) => definition.id !== 'page.root';
  
    const duplicateDefinition = (definition: LandingEditorElementDefinition) => {
      if (definition.kind === 'section' && definition.section) {
        duplicateSection(definition.section);
        return;
      }
  
      const repeatTarget = repeatTargetForDefinition(definition);
      if (repeatTarget) duplicateRepeatTarget(definition, repeatTarget);
    };
  
    const deleteDefinition = (definition: LandingEditorElementDefinition) => {
      if (!canDeleteDefinition(definition)) return;
      if (definition.kind === 'section' && definition.section) {
        deleteSection(definition.section);
        return;
      }
  
      const repeatTarget = repeatTargetForDefinition(definition);
      if (repeatTarget) {
        deleteRepeatTarget(definition, repeatTarget);
        return;
      }
  
      const nextProject = cloneEditorValue(project);
      const frame = nextProject.elementFrames[definition.id] ?? defaultFrame();
      nextProject.elementFrames[definition.id] = { ...frame, hidden: true };
      updateProject(nextProject);
      setSelectedElementId(definition.parentId ?? (definition.section ? `section.${definition.section}` : 'page.root'));
    };
  
    const editorElementIdFromNode = (node: HTMLElement) => [...node.classList].map(className => elementIdByClass[className]).find(Boolean);
  
    const openInlineTextEditor = (elementId: string) => {
      const definition = definitions.find(candidate => candidate.id === elementId);
      if (!definition?.path || definition.kind !== 'text') {
        setInlineTextDraft(null);
        setInlineTextGeometry(null);
        return;
      }
  
      setInlineTextDraft({ elementId, value: fieldValueForDefinition(definition as LandingEditorElementDefinition & { path: string }) });
    };
  
    const handleSectionDragStart = (
      event: DragEvent,
      section: LandingHomeSectionKey,
      intent: 'copy' | 'move' = 'move',
    ) => {
      setDraggedSection(section);
      event.dataTransfer?.setData('text/plain', section);
      if (event.dataTransfer) event.dataTransfer.effectAllowed = intent;
    };
  
    const handleSectionDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = event.dataTransfer.effectAllowed === 'copy' ? 'copy' : 'move';
      }
    };
  
    const handleSectionDrop = (event: DragEvent, targetSection: LandingHomeSectionKey) => {
      event.preventDefault();
      const section = (draggedSection ?? event.dataTransfer?.getData('text/plain')) as LandingHomeSectionKey | undefined;
      if (!section) return;
      placeSection(section, targetSection);
      setDraggedSection(null);
    };
  
    const handlePreviewSectionDrop = (event: DragEvent) => {
      const target = event.target as HTMLElement | null;
      const sectionNode = target?.closest('.vp-editor-section-node') as HTMLElement | null;
      const targetElementId = sectionNode ? editorElementIdFromNode(sectionNode) : null;
      const targetSection = targetElementId?.startsWith('section.')
        ? (targetElementId.slice('section.'.length) as LandingHomeSectionKey)
        : null;
      const section = (draggedSection ?? event.dataTransfer?.getData('text/plain')) as LandingHomeSectionKey | undefined;
      if (!section) return;
      event.preventDefault();
      placeSection(section, targetSection ?? undefined);
      setDraggedSection(null);
    };
  
    const handleElementPointerDown = (event: PointerEvent, elementId: string) => {
      setSelectedElementId(elementId);
      openInlineTextEditor(elementId);
      // Geometry is intentionally locked by default. Movement starts only from the explicit
      // outer-canvas move handle, never from a click or drag on the page content itself.
      event.stopPropagation();
    };
  
    const handlePreviewTextCommit = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      const textNode = target?.closest('.vp-editor-text-node') as HTMLElement | null;
      if (!textNode) return;
      const elementId = editorElementIdFromNode(textNode);
      const definition = elementId ? definitions.find(candidate => candidate.id === elementId) : selectedFieldDefinition;
      if (!definition?.path || definition.kind !== 'text') return;
      const value = normalizeEditableText(textNode.textContent ?? '');
      if (value === fieldValueForDefinition(definition as LandingEditorElementDefinition & { path: string })) return;
      updateField(definition, value);
    };
  
    const commitInlineTextEditor = (draftOverride?: InlineTextDraft) => {
      const nextProject = cloneEditorValue(project) as LandingEditorProject;
      if (
        mergeInlineTextDraftIntoProject(
          nextProject,
          draftOverride ?? inlineTextDraft,
          definitions,
          locale,
          fieldSourceForProject,
          normalizeEditableText,
        )
      ) updateProject(nextProject);
      setInlineTextDraft(null);
      setInlineTextGeometry(null);
    };

    const commitProseMirrorTextEditor = (result: {
      elementId: string;
      value: string;
      styles: LandingEditorInlineTextStyle[];
    }) => {
      if (stageLocalizationTextResult?.(result)) return;
      const definition = definitions.find(candidate => candidate.id === result.elementId);
      if (!definition?.path || definition.kind !== 'text') return;
      const nextProject = cloneEditorValue(project) as LandingEditorProject;
      const textDefinition = definition as LandingEditorElementDefinition & { path: string };
      const value = normalizeEditableText(result.value);
      const previousValue = fieldValueForDefinition(textDefinition);
      setValueAtPath(fieldSourceForProject(nextProject, textDefinition), textDefinition.path, value);
      const frame = nextProject.elementFrames[result.elementId] ?? defaultFrame();
      const stylesByLocale = { ...frame.inlineTextStyles };
      const sourcesByLocale = { ...frame.inlineTextSources };
      if (result.styles.length) {
        stylesByLocale[locale] = result.styles;
        sourcesByLocale[locale] = value;
      } else {
        delete stylesByLocale[locale];
        delete sourcesByLocale[locale];
      }
      nextProject.elementFrames[result.elementId] = {
        ...frame,
        inlineTextStyles: Object.keys(stylesByLocale).length ? stylesByLocale : undefined,
        inlineTextSources: Object.keys(sourcesByLocale).length ? sourcesByLocale : undefined,
      };
      if (value !== previousValue) recordLocalizedFieldCommit?.(textDefinition, previousValue, value);
      updateProject(nextProject);
      setInlineTextDraft(null);
      setInlineTextGeometry(null);
    };

    const applyInlineTextStyle = (change: InlineTextStyleChange) => {
      const definition = definitions.find(candidate => candidate.id === change.elementId);
      if (!definition?.path || definition.kind !== 'text') return;
      const textDefinition = definition as LandingEditorElementDefinition & { path: string };
      const value = normalizeEditableText(change.value);
      const selectedText = normalizeEditableText(change.value.slice(change.start, change.end));
      const normalizedPrefix = normalizeEditableText(change.value.slice(0, change.start));
      let start = normalizedPrefix.length;
      while (start < value.length && value[start] === ' ' && !selectedText.startsWith(' ')) start += 1;
      const end = Math.min(value.length, start + selectedText.length);
      if (!selectedText || start >= end) return;

      const nextProject = cloneEditorValue(project) as LandingEditorProject;
      const frame = nextProject.elementFrames[change.elementId] ?? defaultFrame();
      const previousText = frame.inlineTextSources?.[locale] ?? fieldValueForDefinition(textDefinition);
      const rebased = rebaseInlineTextStyles(frame.inlineTextStyles?.[locale] ?? [], previousText, value);
      const nextStyles = change.patch
        ? patchInlineTextStyleRange(rebased, start, end, change.patch, value.length)
        : replaceInlineTextStyleRange(rebased, start, end, change.style, value.length);
      const stylesByLocale = { ...frame.inlineTextStyles };
      const sourcesByLocale = { ...frame.inlineTextSources };
      if (nextStyles.length) {
        stylesByLocale[locale] = nextStyles;
        sourcesByLocale[locale] = value;
      } else {
        delete stylesByLocale[locale];
        delete sourcesByLocale[locale];
      }
      setValueAtPath(fieldSourceForProject(nextProject, textDefinition), textDefinition.path, value);
      nextProject.elementFrames[change.elementId] = {
        ...frame,
        inlineTextStyles: Object.keys(stylesByLocale).length ? stylesByLocale : undefined,
        inlineTextSources: Object.keys(sourcesByLocale).length ? sourcesByLocale : undefined,
      };
      updateProject(nextProject);
      setInlineTextDraft(null);
      setInlineTextGeometry(null);
    };
  
    const cancelInlineTextEditor = () => {
      setInlineTextDraft(null);
      setInlineTextGeometry(null);
    };
  
    const handleInlineTextKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelInlineTextEditor();
        return;
      }
  
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        commitInlineTextEditor({
          elementId: inlineTextDraft?.elementId ?? selectedFieldDefinition?.id ?? '',
          value: (event.currentTarget as HTMLTextAreaElement).value,
        });
        return;
      }
  
      if (event.key === 'Enter' && !selectedFieldDefinition?.multiline) {
        event.preventDefault();
        commitInlineTextEditor({
          elementId: inlineTextDraft?.elementId ?? selectedFieldDefinition?.id ?? '',
          value: (event.currentTarget as HTMLTextAreaElement).value,
        });
      }
    };
  
    const handlePreviewKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      const target = event.target as HTMLElement | null;
      const textNode = target?.closest('.vp-editor-text-node') as HTMLElement | null;
      if (!textNode) return;
      const elementId = editorElementIdFromNode(textNode);
      const definition = elementId ? definitions.find(d => d.id === elementId) : selectedFieldDefinition;
      if (definition?.multiline) return;
      event.preventDefault();
      textNode.blur();
    };
  
    const handleUndo = () => { if (undoDocument()) setStatus('unsaved'); };
    const handleRedo = () => { if (redoDocument()) setStatus('unsaved'); };

    const changeZoom = (delta: number) => setZoom(clampZoom(zoom + delta));
  
    const toggleSection = (elementId: string) => {
      setExpandedSections({ ...expandedSections, [elementId]: !(expandedSections[elementId] ?? true) });
    };
  return {
    addSection,
    applyInlineTextStyle,
    canDeleteDefinition,
    canDuplicateDefinition,
    changeZoom,
    commitInlineTextEditor,
    commitProseMirrorTextEditor,
    deleteDefinition,
    deleteSection,
    duplicateDefinition,
    duplicateSection,
    handleElementPointerDown,
    handleInlineTextKeyDown,
    handlePreviewKeyDown,
    handlePreviewSectionDrop,
    handlePreviewTextCommit,
    handleRedo,
    handleSectionDragOver,
    handleSectionDragStart,
    handleSectionDrop,
    handleUndo,
    moveSection,
    moveRepeatDefinition,
    moveStaticPageBuiltInSection,
    toggleSection,
  };
};
